
import { computed, defineComponent, PropType, ref, toRefs } from "vue";
import AdmIcon from "../Icon";
import { errorIcon, isValidIcon } from "../config";
import { useId } from "../hooks/identifier";
import "./phone.scss";

const Phone = defineComponent({
  inheritAttrs: false,
  components: {
    AdmIcon,
  },
  props: {
    id: {
      type: String,
      default: "",
      required: true,
    },
    forceId: {
      type: Boolean,
      default: false,
    },
    countries: {
      type: Array as PropType<
        Array<{
          label: string;
          code: string;
          value?: string;
          disabled?: boolean;
        }>
      >,
      required: true,
    },
    phone: {
      type: String,
      default: "",
    },
    code: {
      type: String,
      default: "",
    },
    phoneLabel: {
      type: String,
      required: true,
    },
    countryLabel: {
      type: String,
      required: true,
    },
    error: {
      type: String,
      default: "",
    },
    isValid: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    trailingIcon: {
      type: String,
      default: "",
    },
  },
  emits: ["update:phone", "update:code", "blur", "focus"],
  setup(props, { emit }) {
    const { phone, trailingIcon, error, isValid, disabled } = toRefs(props);
    const identifier = useId(props, "adm-phone");

    const isFocused = ref(false);
    const inputRef = ref();

    const trIcon = computed(() => {
      if (trailingIcon.value) {
        return trailingIcon.value;
      }

      if (error.value) {
        return errorIcon;
      }

      if (isValid.value) {
        return isValidIcon;
      }

      return null;
    });

    const classesInput = computed(() => ({
      "adm-input": true,
      "adm-input__disabled": disabled.value,
      "adm-input__focus": isFocused.value,
      "adm-input__valid": isValid.value,
      "adm-input__error": !!error.value,
      "adm-input__trailing-icon": !!trIcon.value,
      "adm-input__trailing-text": !!error.value,
      "adm-input__floated-label": !!isFocused.value || phone.value,
    }));

    const classesSelect = computed(() => [
      "adm-select",
      {
        "adm-select--disabled": disabled.value,
        "adm-select--valid": isValid.value && !error.value,
        "adm-select--error": !!error.value,
      },
    ]);

    const classesRoot = computed(() => [
      "adm-phone",
      { "adm-phone--focus": isFocused.value },
    ]);

    const onFocus = ($event: Event) => {
      emit("focus", $event);
      isFocused.value = true;
    };

    const onBlur = ($event: Event) => {
      emit("blur", $event);
      isFocused.value = false;
    };

    const onClickLabel = () => {
      inputRef.value.focus();
    };

    const onChangePhone = ($event: Event) => {
      const target = $event.target as HTMLInputElement;
      const value = target.value.replace(/\D/g, "");
      inputRef.value.value = value;
      emit("update:phone", value);
    };

    return {
      trIcon,
      classesRoot,
      classesInput,
      classesSelect,
      identifier,
      onClickLabel,
      onFocus,
      onBlur,
      onChangePhone,
      inputRef,
    };
  },
});

export default Phone;
